import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { store } from '@/store';

export type StateUpdater<T> = (prevState: T) => T;
export type SetterContext<T> = (v: StateUpdater<T> | T) => void;
export type UseSubscriber<T> = [T, SetterContext<T>];
export type StateInterface<T> = {
  setState: (v: StateUpdater<T> | T) => void;
  useState: () => [T, SetterContext<T>];
};
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export const useCreateUseState = <T>(
  selectorCallback: (state: RootState) => T,
  setAction: (payload: T) => {
    type: string;
    payload: T;
  },
  localStoreName?: string
): StateInterface<T> => {
  const state = useSelector(selectorCallback);
  const dispatch = useAppDispatch();

  const setState = (value: StateUpdater<T> | T) => {
    if (typeof value === 'function') {
      const prevState = state;
      const stateUpdater = value as StateUpdater<T>;
      const callbackValue = stateUpdater(prevState);
      localStoreName && window.localStorage.setItem(localStoreName, JSON.stringify(callbackValue));
      dispatch(setAction(callbackValue));
    } else {
      localStoreName && window.localStorage.setItem(localStoreName, JSON.stringify(value));
      dispatch(setAction(value));
    }
  };

  return {
    useState: (): UseSubscriber<T> => [state, setState],
    setState
  };
};
export const useAppDispatch = () => useDispatch<AppDispatch>();

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
